/**
 * Signup tax payload adapter (country-aware contract for Fases 5–6).
 *
 * Phase 4 keeps the public signup UI BR-only; this module is the testable
 * contract that billing-pages / future country picker should call.
 */

import {
  TAX_ID_TYPE_CNPJ,
  TAX_ID_TYPE_ES_TAX_ID,
  TAX_ID_TYPE_NIF,
  cleanTaxId,
  validateTaxId,
} from "../utils/tax-id.js";
import { getCountryCapability, normalizeCountryCode } from "../utils/country-capabilities.js";

export class SignupTaxError extends Error {
  /**
   * @param {string} code
   * @param {string} message
   */
  constructor(code, message) {
    super(message);
    this.name = "SignupTaxError";
    this.code = code;
  }
}

/**
 * @param {object} input
 * @param {string} [input.country]
 * @param {string} [input.taxId]
 * @param {string} [input.companyCnpj] legacy BR alias
 * @param {string} [input.taxIdType] optional; must match registry when set
 * @returns {{ country: string, tax_id: string, tax_id_type: string, company_cnpj?: string }}
 */
export function buildSignupTaxPayload(input = {}) {
  const rawCountry = input.country == null || String(input.country).trim() === ""
    ? "BR"
    : input.country;
  const country = normalizeCountryCode(rawCountry);
  if (!country) {
    throw new SignupTaxError(
      "UNSUPPORTED_COUNTRY",
      "Signup self-serve para este país ainda não está disponível."
    );
  }

  const capability = getCountryCapability(country);
  const taxIdType = String(capability?.taxIdType || TAX_ID_TYPE_CNPJ)
    .trim()
    .toUpperCase();

  if (input.taxIdType != null && String(input.taxIdType).trim()) {
    const clientType = String(input.taxIdType).trim().toUpperCase();
    if (clientType !== taxIdType) {
      throw new SignupTaxError(
        "TAX_ID_TYPE_CONFLICT",
        "tax_id_type não corresponde ao país informado."
      );
    }
  }

  const rawTax = String(input.taxId || input.companyCnpj || "").trim();
  if (!rawTax) {
    throw new SignupTaxError("TAX_ID_REQUIRED", "Identificador fiscal é obrigatório.");
  }

  const cleaned = cleanTaxId(rawTax, taxIdType);
  if (!cleaned || !validateTaxId(cleaned, taxIdType)) {
    if (taxIdType === TAX_ID_TYPE_NIF) {
      throw new SignupTaxError("INVALID_NIF", "NIF inválido");
    }
    if (taxIdType === TAX_ID_TYPE_ES_TAX_ID) {
      throw new SignupTaxError("INVALID_ES_TAX_ID", "Identificador fiscal ES inválido");
    }
    throw new SignupTaxError("INVALID_CNPJ", "CNPJ inválido");
  }

  /** @type {{ country: string, tax_id: string, tax_id_type: string, company_cnpj?: string }} */
  const payload = {
    country,
    tax_id: cleaned,
    tax_id_type: taxIdType,
  };
  // Legacy BR alias for current Pricing Table / onboarding path.
  if (taxIdType === TAX_ID_TYPE_CNPJ) {
    payload.company_cnpj = cleaned;
  }
  return payload;
}

/**
 * BrasilAPI / federal enrichment is BR-only.
 * @param {string} [country]
 * @returns {boolean}
 */
export function shouldLookupBrasilApi(country = "BR") {
  return normalizeCountryCode(country || "BR") === "BR";
}
